import { supabase } from './supabase';

export class ScoreService {
  // Calculate score based on user's onboarding answers
  static async calculateUserScore(userId: string): Promise<number> {
    try {
      // Get user preferences with onboarding answers
      const { data: userPreferences, error } = await supabase
        .from('user_preferences')
        .select('onboarding_answers')
        .eq('user_id', userId)
        .single();

      if (error || !userPreferences?.onboarding_answers) {
        console.log('No onboarding answers found, returning base score');
        return 20; // Base score for new users
      }

      const answers = userPreferences.onboarding_answers;
      let totalScore = 0;

      // Partner questions
      if (answers.has_partner) {
        totalScore += 20;
        if (answers.partner_document_access === 'Yes, they know exactly where') {
          totalScore += 50;
        } else if (answers.partner_document_access === 'They have a general idea') {
          totalScore += 25;
        }
      } else {
        totalScore += 10;
      }

      // Children
      if (answers.children_age_groups && Array.isArray(answers.children_age_groups)) {
        answers.children_age_groups.forEach(ageGroup => {
          if (ageGroup === 'No children') {
            totalScore += 10;
          } else {
            totalScore += 15;
          }
        });
      }

      // Dependents
      if (answers.dependents && Array.isArray(answers.dependents)) {
        answers.dependents.forEach(dependent => {
          if (dependent === 'Elderly parents' || dependent === 'Disabled family member') {
            totalScore += 20;
          } else if (dependent === 'Other dependents') {
            totalScore += 15;
          } else {
            totalScore += 10;
          }
        });
      }

      // Housing
      if (answers.housing_status === 'Own') {
        totalScore += 30;
      } else if (answers.housing_status === 'Rent') {
        totalScore += 20;
      } else {
        totalScore += 10;
      }

      // Property deed access
      if (answers.property_deed_access === 'Very fast - I know exactly where it is') {
        totalScore += 40;
      } else if (answers.property_deed_access === 'Takes some searching') {
        totalScore += 20;
      }

      // Assets
      if (answers.assets && Array.isArray(answers.assets)) {
        answers.assets.forEach(asset => {
          if (asset !== 'None of these') {
            totalScore += 15;
          }
        });
      }

      // Insurance
      if (answers.insurance_types && Array.isArray(answers.insurance_types)) {
        answers.insurance_types.forEach(insurance => {
          if (insurance !== 'None') {
            totalScore += 20;
          }
        });
      }

      // Family policy awareness
      if (answers.family_policy_awareness === 'Yes, they know all the details') {
        totalScore += 30;
      } else if (answers.family_policy_awareness === 'They know we have insurance but not details') {
        totalScore += 15;
      }

      // Legal documents
      if (answers.legal_documents && Array.isArray(answers.legal_documents)) {
        answers.legal_documents.forEach(doc => {
          if (doc !== 'None') {
            totalScore += 25;
          }
        });
      }

      // Document location shared
      if (answers.document_location_shared === 'Yes, they know exactly where everything is') {
        totalScore += 40;
      } else if (answers.document_location_shared === 'They have a general idea') {
        totalScore += 20;
      }

      // Goals
      if (answers.goals && Array.isArray(answers.goals)) {
        answers.goals.forEach(goal => {
          if (goal !== 'None') {
            totalScore += 15;
          }
        });
      }

      // Cap the score at 500
      return Math.min(totalScore, 500);
    } catch (error) {
      console.error('Error calculating user score:', error);
      return 20; // Base score on error
    }
  }

  // Update user's score in the database
  static async updateUserScore(userId: string): Promise<void> {
    try {
      const newScore = await this.calculateUserScore(userId);
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          idecide_score: newScore,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error updating user score:', error);
        throw error;
      }

      console.log(`Updated iDecide score for user ${userId} to ${newScore}`);
    } catch (error) {
      console.error('Error updating user score:', error);
      throw error;
    }
  }
} 